import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'

export type LoyaltyBurnChange =
  | { kind: 'unchanged' }
  /** The points couldn't be priced again in time, or the calculation failed */
  | { kind: 'unavailable' }
  | {
    kind: 'repriced'
    pointsPaid: number
    /** Amount the chosen points covered when the customer chose them */
    previousPointsValue: number
    /** Amount the same points cover now; 0 when they no longer meet the minimum spend */
    pointsValue: number
  }

export const UNCHANGED_LOYALTY_BURN: LoyaltyBurnChange = { kind: 'unchanged' }

function isSameBurn(
  before: App.Checkout.LoyaltyBurningPointItem,
  after: App.Checkout.LoyaltyBurningPointItem | undefined,
): boolean {
  return !!after &&
    after.priceInPoints === before.priceInPoints &&
    after.priceInCurrency === before.priceInCurrency &&
    after.pointsPaid === before.pointsPaid &&
    after.pointsPaidInCurrency === before.pointsPaidInCurrency &&
    after.productType === before.productType
}

/**
 * Loyalty: the price in points follows the room's margin, which the accommodation re-enquiry on Pay
 * can restate without changing the cash price. Compares the points the customer chose with the same
 * points priced again afterwards, so checkout never charges a different points price unseen.
 */
function getLoyaltyBurnChange(
  before: Array<App.Checkout.LoyaltyBurningPointItem>,
  after: Array<App.Checkout.LoyaltyBurningPointItem>,
  isRecalculated: boolean,
): LoyaltyBurnChange {
  if (!before.length) return UNCHANGED_LOYALTY_BURN
  if (!isRecalculated) return { kind: 'unavailable' }

  const afterByItemId = new Map(after.map(item => [item.itemId, item]))
  const isUnchanged = after.length === before.length &&
    before.every(item => isSameBurn(item, afterByItemId.get(item.itemId)))
  if (isUnchanged) return UNCHANGED_LOYALTY_BURN

  return {
    kind: 'repriced',
    pointsPaid: sum(before, item => item.pointsPaid),
    previousPointsValue: floatify(sum(before, item => item.pointsPaidInCurrency)),
    pointsValue: floatify(sum(after, item => item.pointsPaidInCurrency)),
  }
}

export default getLoyaltyBurnChange
